// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Customer table row component module for displaying individual customer entries.
 * Provides a responsive row layout with customer information and interactive elements.
 */
import { IntegrationStatus } from '@/components/integration-status';
import { Link } from '@/components/link';
import { VisibilityOffOutlined } from '@/constants/hardfin-standard-icons';
import { Customer } from '@/customer/api/types';
import { CustomerIcon } from '@/customer/components/customer-icon';
import { pluralize } from '@/utilities/pluralize';
import { Box, Chip, Stack, TableCell, TableRow, Tooltip } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Props for the CustomerTableRow component
 */
interface Props {
  /**
   * The customer data to display in the row.
   * Includes customer details like name, location count, and asset counts.
   */
  customer: Customer;

  hasIntegration: boolean;
}

/**
 * A table row component that displays customer information with interactive elements.
 *
 * Features:
 * - Customer icon and name display
 * - Archive status indication
 * - Responsive design with column hiding on mobile
 * - Interactive links to related assets and locations
 * - Link to customer detail page
 *
 * Visual States:
 * - Regular: Full color display for active customers
 * - Archived: Grayed out display with archive indicator
 *
 * @example
 * ```tsx
 * <CustomerTableRow customer={customerData} />
 * ```
 */
export function CustomerTableRow(props: Props) {
  const queryClient = useQueryClient();

  // Determine colors based on archive status
  const iconColor = props.customer.isArchived ? 'disabled' : 'inherit';

  // Get location IDs for asset filtering
  const locationIds = props.customer.locations.map((location) => location.id);

  return (
    <TableRow>
      {/* Integration cell */}
      {props.hasIntegration && (
        <TableCell
          width="auto"
          color={iconColor}
          sx={{
            width: '3rem',
            pl: 2,
            pr: 0,
            whiteSpace: 'nowrap',
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="center">
            <IntegrationStatus
              status={props.customer.integrations}
              entityType="company"
              entityKey={props.customer.key}
              entityDisplayName={props.customer.name}
              onResolved={() => {
                // Listing pages key on ['customer', filter] — same
                // prefix as the detail query. A prefix invalidate
                // covers every list filter currently mounted.
                void queryClient.invalidateQueries({
                  queryKey: ['customer'],
                });
              }}
            />
          </Box>
        </TableCell>
      )}

      {/* Customer Icon Cell */}
      <TableCell width="3rem" color={iconColor}>
        <Box display="flex" alignItems="center" justifyContent="center">
          <CustomerIcon customer={props.customer} />
        </Box>
      </TableCell>

      {/* Customer Name Cell */}
      <TableCell>
        <Stack spacing={1} direction="row" alignItems="center">
          <Link
            to="/companies/$companyKey"
            params={{ companyKey: props.customer.key }}
            variant="subtitle2"
            color="primary.main"
          >
            {props.customer.name}
          </Link>
          {props.customer.isArchived && (
            <Tooltip title="This company is archived" arrow>
              <VisibilityOffOutlined fontSize="small" color="disabled" />
            </Tooltip>
          )}
        </Stack>
      </TableCell>

      {/* Role Cell - Hidden on mobile */}
      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
        <Stack direction="row" spacing={1}>
          <Tooltip
            arrow
            title={`${props.customer.name} is ${props.customer.isCustomer ? '' : 'not'} a customer`}
          >
            <Chip
              size="small"
              label="Customer"
              color="secondary"
              variant={props.customer.isCustomer ? 'filled' : 'outlined'}
            />
          </Tooltip>
          <Tooltip
            arrow
            title={`${props.customer.name} is ${props.customer.isSupplier ? '' : 'not'} a supplier`}
          >
            <Chip
              size="small"
              label="Supplier"
              color="secondary"
              variant={props.customer.isSupplier ? 'filled' : 'outlined'}
            />
          </Tooltip>
        </Stack>
      </TableCell>

      {/* Locations Count Cell - Hidden on mobile */}
      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
        <Link
          to="/locations"
          variant="body2"
          color="text.primary"
          search={{ customers: [props.customer.id] }}
        >
          {pluralize(props.customer.locationCount, { singular: 'location' })}
        </Link>
      </TableCell>

      {/* Assets at Location Count Cell - Hidden on mobile */}
      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
        <Link
          to="/assets"
          variant="body2"
          color="text.primary"
          search={{ locations: locationIds }}
        >
          {pluralize(props.customer.assetsAtLocation, { singular: 'asset' })}
        </Link>
      </TableCell>

      {/* Total Assets Count Cell - Hidden on mobile */}
      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
        <Link
          to="/assets"
          variant="body2"
          color="text.primary"
          search={{ customers: [props.customer.id] }}
        >
          {pluralize(props.customer.assetCount, { singular: 'asset' })}
        </Link>
      </TableCell>
    </TableRow>
  );
}
